import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation } from "@apollo/client/react";
import { useLocation, useNavigate } from "react-router-dom";
import themestyle from "../theme";
import AddInstructorForm from "../components/AddInstructorForm";
import AllInstructorsTable from "../components/AllInstructorsTable";
import ConfirmDialog from "../components/confirmdialogbox";
import CustomSnackbar from "../components/Snackbar";
import { DELETE_INSTRUCTOR, UPDATE_APPROVAL } from "../graphql/mutation";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { MSG } from "../utils/const";
import { GET_INSTRUCTOR_ENGAGEMENT_REPORT, GET_INSTRUCTOR_LIST } from "../graphql/query";

const PAGE_TITLE = "Instructors - United Grooves";

type ViewType = "ADD_INSTRUCTOR" | "ALL_INSTRUCTORS" | "VIEW_INSTRUCTOR" | "EDIT_INSTRUCTOR";

const InstructorManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAddRoute = location.pathname === "/instructors/add";
  const [activeView, setActiveView] = useState<ViewType>(() => (isAddRoute ? "ADD_INSTRUCTOR" : "ALL_INSTRUCTORS"));
  const [selectedInstructorId, setSelectedInstructorId] = useState<string | undefined>();
  const [refreshCount, setRefreshCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instructorToDelete, setInstructorToDelete] = useState<{ id: string; name: string } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("success");
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<{ id: string; name: string; currentStatus: boolean } | null>(null);

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  useEffect(() => {
    setActiveView(isAddRoute ? "ADD_INSTRUCTOR" : "ALL_INSTRUCTORS");
    setSelectedInstructorId(undefined);
  }, [isAddRoute]);

  const [deleteInstructor] = useMutation(DELETE_INSTRUCTOR, {
    refetchQueries: [{ query: GET_INSTRUCTOR_LIST, variables: { page: 1, perPage: 100 } }, { query: GET_INSTRUCTOR_ENGAGEMENT_REPORT }],
  });
  const [updateApproval] = useMutation(UPDATE_APPROVAL, {
    refetchQueries: [{ query: GET_INSTRUCTOR_LIST, variables: { page: 1, perPage: 100 } }, { query: GET_INSTRUCTOR_ENGAGEMENT_REPORT }],
  });

  const handleView = (id: string) => {
    setSelectedInstructorId(id);
    setActiveView("VIEW_INSTRUCTOR");
  };

  const handleEdit = (id: string) => {
    setSelectedInstructorId(id);
    setActiveView("EDIT_INSTRUCTOR");
  };

  const handleBack = () => {
    navigate("/instructors");
    setActiveView("ALL_INSTRUCTORS");
    setSelectedInstructorId(undefined);
  };

  const handleSuccess = () => {
    navigate("/instructors");
    setActiveView("ALL_INSTRUCTORS");
    setSelectedInstructorId(undefined);
    setRefreshCount((prev) => prev + 1);
  };

  const handleStatusClick = (id: string, name: string, currentStatus: boolean) => {
    setStatusTarget({ id, name, currentStatus });
    setStatusConfirmOpen(true);
  };

  const handleConfirmStatus = async () => {
    if (!statusTarget) return;
    try {
      const { data: response }: any = await updateApproval({
        variables: { id: statusTarget.id, approval: !statusTarget.currentStatus },
      });
      
      if (response?.updateApproval?.success) {
        setSnackbarMessage(MSG.STATUS_UPDATED_SUCCESS);
        setSnackbarType("success");
        setRefreshCount((prev) => prev + 1);
      } else {
        setSnackbarMessage(response?.updateApproval?.message || "Failed to update status");
        setSnackbarType("error");
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Failed to update status");
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setStatusConfirmOpen(false);
      setStatusTarget(null);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setInstructorToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!instructorToDelete) return;
    try {
      const { data: response }: any = await deleteInstructor({
        variables: { id: instructorToDelete.id },
      });
      if (response?.deleteInstructor?.success) {
        setSnackbarMessage(response.deleteInstructor.message || "Instructor deleted");
        setSnackbarType("success");
        setSnackbarOpen(true);
        handleSuccess();
      } else {
        setSnackbarMessage(response?.deleteInstructor?.message || "Failed to delete instructor");
        setSnackbarType("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error(err);
      setSnackbarMessage("Something went wrong");
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
      setInstructorToDelete(null);
    }
  };

  const isAllView = activeView === "ALL_INSTRUCTORS";
  const isAddView = activeView === "ADD_INSTRUCTOR";

  return (
    <Box sx={{ mt: { xs: 1, sm: 3, md: 3 }, px: { xs: 1, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: { xs: 2, md: 0 },
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
          {(activeView === "VIEW_INSTRUCTOR" || activeView === "EDIT_INSTRUCTOR") && (
            <Box
              onClick={handleBack}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                color: "#6B7280",
                fontSize: "14px",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />
              Back
            </Box>
          )}
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: "24px" }}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            {activeView === "VIEW_INSTRUCTOR"
              ? "View Instructor"
              : activeView === "EDIT_INSTRUCTOR"
              ? "Edit Instructor"
              : "Instructors"}
          </Typography>
        </Box>

        {(activeView === "ADD_INSTRUCTOR" || activeView === "ALL_INSTRUCTORS") && (
          <Box
            sx={{
              display: "flex",
              backgroundColor: themestyle.colors.white,
              borderRadius: "6px",
              padding: "6px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
              width: { xs: "100%", md: "fit-content" },
              justifyContent: { xs: "space-between", md: "flex-start" },
            }}
          >
            <Box
              onClick={() => {
                navigate("/instructors");
                setActiveView("ALL_INSTRUCTORS");
                setSelectedInstructorId(undefined);
              }}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: isAllView ? "#4CAF50" : "#6B7280",
                backgroundColor: isAllView ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              All Instructors
            </Box>
            <Box
              onClick={() => {
                navigate("/instructors/add");
                setActiveView("ADD_INSTRUCTOR");
                setSelectedInstructorId(undefined);
              }}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1.5 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "6px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: isAddView ? "#4CAF50" : "#6B7280",
                backgroundColor: isAddView ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              Add Instructor
            </Box>
          </Box>
        )}
      </Box>

      {activeView === "ADD_INSTRUCTOR" && (
        <AddInstructorForm onSuccess={handleSuccess} onCancel={() => navigate("/instructors")} />
      )}

      {activeView === "ALL_INSTRUCTORS" && (
        <AllInstructorsTable
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onStatusConfirm={handleStatusClick}
          refreshTrigger={refreshCount}
        />
      )}

      {activeView === "VIEW_INSTRUCTOR" && selectedInstructorId && (
        <AddInstructorForm
          mode="view"
          id={selectedInstructorId}
          onCancel={handleBack}
          onEdit={() => setActiveView("EDIT_INSTRUCTOR")}
        />
      )}

      {activeView === "EDIT_INSTRUCTOR" && selectedInstructorId && (
        <AddInstructorForm
          mode="edit"
          id={selectedInstructorId}
          onSuccess={handleSuccess}
          onCancel={handleBack}
          onDelete={handleDeleteClick}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        icon={<DeleteOutlineIcon sx={{ color: "#EB2857" }} />}
        message={`Are you sure you want to delete ${instructorToDelete?.name || "this instructor"}?`}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="danger"
        iconBgColor="danger"
      />

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />

      <ConfirmDialog
        open={statusConfirmOpen}
        icon={
          statusTarget?.currentStatus ? (
            <CancelIcon sx={{ fontSize: 50, color: "#d32f2f" }} />
          ) : (
            <CheckCircleIcon sx={{ fontSize: 50, color: "#A3D451" }} />
          )
        }
        message={
          statusTarget
            ? `${MSG.CONFIRM_ACTION_PREFIX} ${statusTarget.currentStatus ? "deactivate" : "activate"} ${
                statusTarget.name
              }?`
            : ""
        }
        onConfirm={handleConfirmStatus}
        onClose={() => setStatusConfirmOpen(false)}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonColor={statusTarget?.currentStatus ? "#EB2857" : "#A3D451"}
      />
    </Box>
  );
};

export default InstructorManagement;
